import { Button } from "@/components/ui/button";
import { ArrowRight, Play, Terminal, Code2, GitBranch, Sparkles, Zap } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export const HeroSection = () => {
  const navigate = useNavigate();
  const cubeRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [particlePositions, setParticlePositions] = useState<Array<{x: number, y: number, delay: number}>>([]);

  // Initialize particle positions
  useEffect(() => {
    const particles = Array.from({ length: 20 }, (_, i) => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 5,
    }));
    setParticlePositions(particles);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 40;
      const y = (e.clientY / window.innerHeight - 0.5) * 40;
      setMousePos({ x, y });

      // Create interactive glow effect
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        const xPercent = ((e.clientX - rect.left) / rect.width) * 100;
        const yPercent = ((e.clientY - rect.top) / rect.height) * 100;
        
        heroRef.current.style.setProperty('--mouse-x', `${xPercent}%`);
        heroRef.current.style.setProperty('--mouse-y', `${yPercent}%`);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <section 
      ref={heroRef}
      className="relative min-h-[100vh] flex items-center pt-20 pb-32 overflow-hidden group px-4 sm:px-6 lg:px-8 xl:px-12"
      style={{
        background: `radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(139, 92, 246, 0.1) 0%, transparent 50%)`,
      }}
    >
      {/* Enhanced Animated Particle Background */}
      <div className="absolute inset-0">
        {/* Larger Gradient Mesh */}
        <div className="absolute top-0 left-1/4 w-[800px] h-[800px] bg-primary/25 rounded-full blur-[150px] animate-pulse group-hover:scale-110 transition-transform duration-1000" />
        <div className="absolute bottom-0 right-1/4 w-[700px] h-[700px] bg-accent/20 rounded-full blur-[130px] animate-pulse group-hover:scale-110 transition-transform duration-1000" style={{ animationDelay: "1s" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-purple-500/15 rounded-full blur-[180px] group-hover:opacity-80 transition-opacity duration-1000" />
        
        {/* Interactive Animated Grid */}
        <div 
          className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.2)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border)/0.2)_1px,transparent_1px)] bg-[size:60px_60px] transition-opacity duration-500"
          style={{
            opacity: isHovered ? 0.8 : 0.4,
            transform: `translate(${mousePos.x * 0.1}px, ${mousePos.y * 0.1}px)`,
          }}
        />
        
        {/* Enhanced Floating Particles with Mouse Interaction */}
        <div className="absolute inset-0 overflow-hidden">
          {particlePositions.map((particle, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-primary/50 rounded-full animate-float cursor-pointer hover:scale-150 hover:bg-primary transition-all duration-300"
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                animationDelay: `${particle.delay}s`,
                animationDuration: `${6 + Math.random() * 8}s`,
                transform: `translate(${mousePos.x * (i % 3) * 0.5}px, ${mousePos.y * (i % 3) * 0.5}px)`,
              }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            />
          ))}
        </div>

        {/* Interactive Glow Orbs */}
        <div 
          className="absolute w-32 h-32 bg-primary/30 rounded-full blur-3xl transition-all duration-500"
          style={{
            left: `calc(var(--mouse-x, 50%) - 64px)`,
            top: `calc(var(--mouse-y, 50%) - 64px)`,
            opacity: isHovered ? 0.6 : 0.3,
          }}
        />
      </div>

      <div className="container mx-auto relative z-10">
        <div className="grid lg:grid-cols-[1.3fr_0.7fr] gap-16 items-center">
          {/* Left Content - Enhanced */}
          <div className="text-left max-w-3xl space-y-8">
            {/* Badge - More Interactive */}
            <div 
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full glass border border-primary/30 mb-8 animate-fade-in opacity-0 hover:border-primary/60 hover:scale-105 transition-all duration-300 cursor-pointer group/badge" 
              style={{ animationFillMode: 'forwards' }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary group-hover/badge:scale-125 transition-transform"></span>
              </span>
              <span className="text-sm font-medium group-hover/badge:text-primary transition-colors">Now available for enterprise and startups</span>
              <Sparkles className="w-3.5 h-3.5 text-primary opacity-0 group-hover/badge:opacity-100 transition-opacity" />
            </div>

            {/* Main Heading */}
            <h1 
              className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-heading font-bold mb-6 animate-fade-in leading-[1.1] opacity-0 hover:scale-[1.02] transition-transform duration-500 cursor-default" 
              style={{ animationDelay: "0.1s", animationFillMode: 'forwards' }}
            >
              Build what's
              <span className="block gradient-text hover:scale-105 inline-block transition-transform duration-300">
                next.
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg md:text-xl lg:text-2xl text-muted-foreground mb-8 animate-fade-in leading-relaxed opacity-0 max-w-2xl" style={{ animationDelay: "0.2s", animationFillMode: 'forwards' }}>
            From concept to scalable execution, we design, build, and support high-performance apps, 
            websites, and cloud systems with dedicated teams and long-term commitment.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12 animate-fade-in opacity-0" style={{ animationDelay: "0.3s", animationFillMode: 'forwards' }}>
              <Button 
                size="lg" 
                className="text-lg md:text-xl px-8 md:px-10 h-14 md:h-16 glow-primary bg-gradient-to-r from-primary to-purple-600 hover:opacity-90 hover:scale-105 hover:shadow-2xl transition-all duration-300 group/btn relative overflow-hidden"
                onMouseEnter={() => setIsHovered(true)}
                onClick={() => navigate("/contact#contact-form")}
              >
                <span className="relative z-10 flex items-center">
                  Get Started
                  <ArrowRight className="w-5 h-5 md:w-6 md:h-6 ml-2 group-hover/btn:translate-x-2 transition-transform duration-300" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-accent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300" />
              </Button>
              {/* <Button 
                size="lg" 
                variant="outline" 
                className="text-lg md:text-xl px-8 md:px-10 h-14 md:h-16 glass border-border/50 hover:bg-primary/10 hover:border-primary/50 hover:scale-105 transition-all duration-300 group/btn2"
                onMouseEnter={() => setIsHovered(true)}
                onClick={() => navigate("/contact#contact-form")}
              >
                <Play className="w-5 h-5 md:w-6 md:h-6 mr-2 group-hover/btn2:scale-125 transition-transform duration-300" />
                Watch Demo
              </Button> */}
            </div>

            {/* Code Snippet Preview */}
            <div 
              className="glass-card rounded-xl p-5 max-w-lg animate-fade-in opacity-0 hover:scale-[1.02] hover:border-primary/50 transition-all duration-300 cursor-pointer group/code" 
              style={{ animationDelay: "0.4s", animationFillMode: 'forwards' }}
              onMouseEnter={() => setIsHovered(true)}
            >
              <div className="flex items-center gap-2 mb-3">
                <Terminal className="w-5 h-5 text-primary group-hover/code:scale-110 transition-transform" />
                <span className="text-sm text-muted-foreground font-mono group-hover/code:text-primary transition-colors">Quick start</span>
                <Zap className="w-4 h-4 text-accent opacity-0 group-hover/code:opacity-100 transition-opacity" />
              </div>
              <code className="text-base font-mono text-foreground group-hover/code:text-primary transition-colors">
                <span className="text-muted-foreground">$</span>{" "}
                <span className="text-primary group-hover/code:text-accent transition-colors">npx</span>{" "}
                <span className="text-accent group-hover/code:text-primary transition-colors">create-techbus-app</span>{" "}
                <span className="text-muted-foreground">my-project</span>
              </code>
            </div>
          </div>

          {/* Right - Enhanced 3D Cube - Moderately Larger */}
          <div 
            className="relative flex items-center justify-center lg:justify-end"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div 
              ref={cubeRef}
              className="relative w-96 h-96 md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] perspective-1000 cursor-pointer group/cube"
              style={{
                transform: `rotateY(${mousePos.x}deg) rotateX(${-mousePos.y}deg) scale(${isHovered ? 1.05 : 1})`,
                transition: "transform 0.15s ease-out",
              }}
            >
              {/* 3D Floating Cube */}
              <div className="absolute inset-0 transform-style-3d animate-spin-slow group-hover/cube:animate-none">
                {/* Main Cube Face */}
                <div className="absolute inset-10 md:inset-12 lg:inset-14 glass border-2 border-primary/40 rounded-3xl transform rotate-y-0 translate-z-25 flex items-center justify-center group-hover/cube:border-primary/70 group-hover/cube:scale-110 transition-all duration-300">
                  <Code2 className="w-24 h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 text-primary group-hover/cube:scale-125 group-hover/cube:rotate-12 transition-all duration-300" />
                </div>
                {/* Secondary Face */}
                <div className="absolute inset-14 md:inset-16 lg:inset-18 glass border-2 border-accent/40 rounded-2xl transform rotate-12 flex items-center justify-center group-hover/cube:border-accent/70 group-hover/cube:scale-110 transition-all duration-300">
                  <GitBranch className="w-18 h-18 md:w-22 md:h-22 lg:w-26 lg:h-26 text-accent group-hover/cube:scale-125 transition-transform duration-300" />
                </div>
                {/* Tertiary Face */}
                <div className="absolute inset-18 md:inset-20 lg:inset-22 glass border-2 border-purple-500/40 rounded-xl transform -rotate-6 flex items-center justify-center group-hover/cube:border-purple-500/70 group-hover/cube:scale-110 transition-all duration-300">
                  <Terminal className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 text-purple-400 group-hover/cube:scale-125 transition-transform duration-300" />
                </div>
              </div>

              {/* Enhanced Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-transparent to-accent/30 rounded-full blur-[60px] md:blur-[80px] lg:blur-[100px] group-hover/cube:blur-[120px] transition-all duration-500" />
              
              {/* More Orbiting Elements */}
              <div className="absolute inset-0 animate-spin-slow" style={{ animationDuration: "15s" }}>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 bg-primary rounded-full glow-primary group-hover/cube:scale-150 transition-transform duration-300" />
              </div>
              <div className="absolute inset-0 animate-spin-slow" style={{ animationDuration: "20s", animationDirection: "reverse" }}>
                <div className="absolute bottom-0 right-0 w-5 h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 bg-accent rounded-full glow-accent group-hover/cube:scale-150 transition-transform duration-300" />
              </div>
              <div className="absolute inset-0 animate-spin-slow" style={{ animationDuration: "25s" }}>
                <div className="absolute top-1/2 right-0 translate-x-1/2 w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 bg-purple-500 rounded-full group-hover/cube:scale-150 transition-transform duration-300" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Scroll Indicator */}
      <div 
        className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce-subtle cursor-pointer group/scroll"
        onMouseEnter={() => setIsHovered(true)}
        onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
      >
        <div className="w-7 h-12 rounded-full border-2 border-primary/40 group-hover/scroll:border-primary flex items-start justify-center p-1.5 transition-colors">
          <div className="w-2 h-4 rounded-full bg-primary group-hover/scroll:bg-accent animate-bounce transition-colors" />
        </div>
        <p className="text-xs text-muted-foreground mt-2 text-center opacity-0 group-hover/scroll:opacity-100 transition-opacity">
          Scroll to explore
        </p>
      </div>
    </section>
  );
};